import React, { useContext, useEffect, useRef, useMemo, useState } from "react";

import { Icon } from "components/Icon";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import styled, { css } from "styled-components";

import { NEWS_SET_READ_API, NEWS_LIST_DIALER_API } from "const/apiMethods";
import { getCountDialer, getNewsDialer } from "actions/news_management";
import { checkPermissionsFor } from "store/storeHelpers";
import { APP_TYPE_ADMIN } from "const";
import { api } from "api/loginRoutes";
import { AppTypeContext, RtlContext } from "App";
import { Spacer } from "./base/Spacer";
import m from "definedMessages";


import { Button, ButtonGroup, Message } from "rsuite";

const LINE_HEIGHT = 20;
const MAX_LINES = 3;

const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES;

const hasVisibleHtmlContent = (html = "") => {
    const text = `${html}`
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();

    return !!text;
};

const getPostText = (post, rtl) => {
    if (!post) {
        return "";
    }

    const localizedText = rtl ? post.text_arabic : post.text;

    return hasVisibleHtmlContent(localizedText)
        ? localizedText
        : post.text || "";
};

const NewsWidget = (
    {
        post: widgetPost,
        postList,
        loading,
        permissions,
        getNewsDialer,
        getCountDialer,
    }
) => {

    const { formatMessage } = useIntl();
    const textRef = useRef(null);
    const [excerpted, setExcerpted] = useState(false);
    const appType = useContext(AppTypeContext);
    const rtl = useContext(RtlContext);
    const [post, setPost] = useState(widgetPost);
    const [postCount, setPostCount] = useState(null);
    const postText = useMemo(() => getPostText(post, rtl), [post, rtl]);

    const unreadPostList = useMemo(() => {
        if (!postList) return [];

        const unreadPosts = postList.filter(p => !p.read);
        return unreadPosts;
    }, [postList]);

    useEffect(() => {
        getNewsDialer(1, 100, false);
    }, []);

    useEffect(() => {
        const postCount = unreadPostList.findIndex(p => p.id === widgetPost?.id);
        setPost(unreadPostList[postCount]);
        setPostCount(postCount);
    }, [unreadPostList, widgetPost]);

    useEffect(() => {
        if (postCount === null || !unreadPostList.length) {
            return;
        }

        setPost(unreadPostList[postCount]);
    }, [postCount, unreadPostList]);


    useEffect(() => {
        if (!textRef?.current) {
            return;
        }

        const height = textRef.current.offsetHeight;
        if (height > MAX_HEIGHT) {
            setExcerpted(true);
            return;
        }

        setExcerpted(false);
    }, [postText]);

    useEffect(() => {
        if (checkPermissionsFor(NEWS_LIST_DIALER_API)) {
            getCountDialer();
        }
    }, [permissions]);


    const showPreviousPost = () => {
        if (postCount === 0) {
            return;
        }
        const newCount = postCount - 1;
        setPostCount(newCount);
        setPost(unreadPostList[newCount]);
    };

    const showNextPost = () => {
        if (postCount >= unreadPostList.length - 1) {
            return;
        }
        const newCount = postCount + 1;
        setPostCount(newCount);
        setPost(unreadPostList[newCount]);
    };

    if (appType === APP_TYPE_ADMIN || !post) {
        return null;
    }

    const handleClose = () => {
        api(NEWS_SET_READ_API, { target: { news_id: post.id } })
            .then(r => {
                if (!r) {
                    return;
                }
                setPost(null);
                getCountDialer();
            });
    };

    const postCountTotal = unreadPostList.length;
    const postCountFirst = 0;
    const postCountLast = postCountTotal - 1;
    const postCountEven = postCountTotal % 2 === 0;
    const postCountMid = Math.floor(postCountTotal / 2);

    return (
        <>
            <StyledMessage
                closable
                type="success"
                onClose={handleClose}
            >
                <StyledText withReadMore={excerpted}>
                    <div
                        ref={textRef}
                        dangerouslySetInnerHTML={{ __html: postText }}
                    />
                </StyledText>
                {(textRef.current && textRef.current.offsetHeight <= MAX_HEIGHT || !textRef.current)
                    ? <></>
                    : (excerpted ? <StyledReadMore>
                        <Button
                            onClick={() => setExcerpted(false)}
                            appearance="link"
                            size="xs"
                        >
                            {formatMessage(m.readMore)}
                        </Button>
                    </StyledReadMore>
                        : <StyledReadMore>
                            <Button
                                onClick={() => setExcerpted(true)}
                                appearance="link"
                                size="xs"
                            >
                                <Icon icon="chevron-up" />
                            </Button>
                        </StyledReadMore>
                    )
                }
                {postCountTotal > 1 && <PostProgressBar rtl={rtl}>
                    <Button onClick={showPreviousPost} appearance="ghost" disabled={postCount === 0 || loading}>
                        {"<"}
                    </Button>
                    <PostProgressGroup>
                        <PostProgress active={postCount === postCountFirst} />
                        {postCountTotal > 3 ? <PostProgress active={postCount > postCountFirst && postCount < postCountMid} /> : <></>}
                        {!postCountEven ? <PostProgress active={postCount === postCountMid} /> : <></>}
                        {postCountTotal > 3 ? <PostProgress active={postCountEven ? (postCount >= postCountMid && postCount < postCountLast) : (postCount > postCountMid && postCount < postCountLast)} /> : <></>}
                        <PostProgress active={postCount === postCountLast} />
                    </PostProgressGroup>
                    <Button onClick={showNextPost} appearance="ghost" disabled={postCount >= unreadPostList.length - 1 || loading}>
                        {">"}
                    </Button>
                </PostProgressBar>}
            </StyledMessage>
            <Spacer />
        </>
    );
};

const PostProgress = ({ active }) => (
    <Button disabled appearance="subtle">{active ? "●" : "○"}</Button>
);

const mapState = ({ auth, news_management }) => ({
    post: news_management.widgetPost,
    postList: news_management.listDialer,
    loading: news_management.loading,
    permissions: auth.permissions
});

export default connect(mapState, {
    getNewsDialer,
    getCountDialer
})(NewsWidget);

const PostProgressBar = styled(ButtonGroup)`
    && {
        display: flex;
        ${props => props.rtl ? css`
            flex-direction: row-reverse;
        ` : css`
            flex-direction: row;
        `};
       
        align-items: center;
        justify-content: center;

        button {
            height: 28px;
            padding: 4px 12px;
            border-color: var(--color-green) !important;
            color: var(--color-green) !important;
        }

        button:disabled {
            color: var(--color-green) !important;
            opacity: 0.5;
        }
    }
`;

const PostProgressGroup = styled.span`
    display: inline;
`;

const StyledMessage = styled(Message)`
    && {        
        .rs-message-btn-close {
            color: var(--color-error) !important;

            .rtl & {
                left: 0;
                right: auto;
            }
        }
    }
`;


const StyledText = styled.div`
    ${props => props.withReadMore && css`
        max-height: ${MAX_HEIGHT}px; 
        overflow: hidden;
    `}
`;


const StyledReadMore = styled.div`
    width: 100%;
    text-align: center;
    margin-top: 20px;
    margin-bottom: 5px;
    .rs-btn {
        color: #4caf50 !important;
    }
`;
